import { Slot } from '@radix-ui/react-slot';
import {
  type ComponentPropsWithoutRef,
  cloneElement,
  type ElementRef,
  forwardRef,
  isValidElement,
} from 'react';
import { composeVariants, cva, cx, type VariantProps } from '../../utils/cva';
import { jsxTruthy } from '../../utils/jsx-truthy';
import { useFocusVisible } from '../../utils/use-focus-visible';
import { dataFocusRing } from '../FocusVisible';
import {
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from '../ScrollArea';

const overlayVariants = cva({
  base: [
    'fixed',
    'inset-0',
    'bg-overlay-dark',
    'flex',
    'items-center',
    'justify-center',
    'p-4',
  ],
  variants: {
    transition: {
      true: [
        'data-[state=open]:animate-in',
        'data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0',
      ],
    },
  },
});

export interface DialogOverlayProps
  extends ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof overlayVariants> {
  'data-state'?: 'open' | 'closed';
  'data-testid'?: string;
}

export const DialogOverlay = forwardRef<ElementRef<'div'>, DialogOverlayProps>(
  (props, ref) => {
    const {
      className,
      transition = true,
      'data-testid': dataTestId = 'dialog-overlay',
      ...rest
    } = props;

    return (
      <div
        ref={ref}
        data-testid={dataTestId}
        className={overlayVariants({ transition, className })}
        {...rest}
      />
    );
  },
);

export const DialogContainer = (props: {
  children: React.ReactNode;
  'data-testid'?: string;
}) => {
  const { children, 'data-testid': dataTestId = 'dialog-container' } = props;

  return <div data-testid={dataTestId}>{children}</div>;
};

const dialogVariants = composeVariants(
  dataFocusRing,
  cva({
    base: [
      'fixed',
      'inset-0',
      'm-auto',
      'h-[max-content]',
      'flex',
      'flex-col',
      'w-[calc(100%-var(--space-4)*2)]',
      'max-h-[calc(100%-var(--space-4)*2)]',
      'bg-default',
      'shadow-lg',
      'rounded-xl',
      'border-subtle',
      'border',
      '[--dialog-padding-x:var(--space-6)]',
    ],
    variants: {
      transition: {
        true: [
          'duration-200',
          'data-[state=open]:animate-in',
          'data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0',
          'data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95',
          'data-[state=open]:zoom-in-95',
        ],
      },
      size: {
        sm: ['max-w-[528px]'],
        md: ['max-w-[712px]'],
        lg: ['max-w-[896px]'],
        full: ['max-w-full max-h-[calc(100%)]'],
      },
    },
  }),
);

export interface DialogProps
  extends ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof dialogVariants> {
  'data-testid'?: string;
}

export const Dialog = forwardRef<ElementRef<'div'>, DialogProps>(
  (props, ref) => {
    const {
      className,
      children,
      transition = true,
      onFocus,
      onBlur,
      size = 'md',
      'data-testid': dataTestId = 'dialog-root',
      ...rest
    } = props;

    return (
      <div
        ref={ref}
        data-testid={dataTestId}
        className={cx(dialogVariants({ transition, size }), className)}
        {...useFocusVisible({ onFocus, onBlur })}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

export interface DialogHeaderProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  asChild?: boolean;
  'data-testid'?: string;
}

export const DialogHeader = forwardRef<ElementRef<'div'>, DialogHeaderProps>(
  (props, forwardedRef) => {
    const {
      className,
      title,
      description,
      action,
      asChild,
      children,
      'data-testid': dataTestId = 'dialog-header',
      ...rest
    } = props;

    const titleRowElement = (
      <div className="flex-none flex gap-2 items-center">
        <span
          data-title
          data-testid={`${dataTestId}-title`}
          className="flex-1 py-1 min-w-0 truncate font-semibold text-xl"
        >
          {title}
        </span>
        {jsxTruthy(action) ? (
          <span
            data-action
            data-testid={`${dataTestId}-action`}
            className="flex-none flex gap-2 items-center"
          >
            {action}
          </span>
        ) : null}
      </div>
    );

    const descriptionElement = jsxTruthy(description) ? (
      <div
        data-description
        data-testid={`${dataTestId}-description`}
        className="text-sm text-subtle"
      >
        {description}
      </div>
    ) : null;

    const c = cx(
      'flex-none',
      'px-[var(--dialog-padding-x)]',
      'pt-6',
      'pb-3',
      'flex',
      'flex-col',
      'gap-2',
      className,
    );

    if (asChild && isValidElement(children)) {
      return (
        <Slot ref={forwardedRef} className={c} {...rest}>
          {cloneElement(
            children,
            undefined,
            titleRowElement,
            descriptionElement,
          )}
        </Slot>
      );
    }

    return (
      <div ref={forwardedRef} data-testid={dataTestId} className={c} {...rest}>
        {titleRowElement}
        {descriptionElement}
      </div>
    );
  },
);

export interface DialogContentProps extends ComponentPropsWithoutRef<'div'> {
  'data-testid'?: string;
}

export const DialogContent = forwardRef<ElementRef<'div'>, DialogContentProps>(
  (props, ref) => {
    const {
      className,
      'data-testid': dataTestId = 'dialog-content',
      ...rest
    } = props;

    return (
      <div
        ref={ref}
        data-testid={dataTestId}
        className={cx(
          'flex-auto',
          'min-h-0',
          'px-[var(--dialog-padding-x)]',
          'py-4',
          'overflow-y-auto',
          className,
        )}
        {...rest}
      />
    );
  },
);

export interface DialogScrollAreaProps
  extends ComponentPropsWithoutRef<typeof ScrollAreaRoot> {
  'data-testid'?: string;
}

export const DialogScrollArea = forwardRef<
  ElementRef<typeof ScrollAreaRoot>,
  DialogScrollAreaProps
>((props, forwardedRef) => {
  const {
    children,
    className,
    'data-testid': dataTestId = 'dialog-scrollarea',
    ...rest
  } = props;

  return (
    <ScrollAreaRoot
      ref={forwardedRef}
      data-testid={dataTestId}
      className={cx('min-h-0', className)}
      {...rest}
    >
      <ScrollAreaViewport
        className={cx('px-[var(--dialog-padding-x)]', 'py-4')}
      >
        {children}
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="vertical">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollAreaRoot>
  );
});

export interface DialogFooterProps extends ComponentPropsWithoutRef<'div'> {
  'data-testid'?: string;
}

export const DialogFooter = forwardRef<ElementRef<'div'>, DialogFooterProps>(
  (props, forwardedRef) => {
    const {
      className,
      'data-testid': dataTestId = 'dialog-footer',
      ...rest
    } = props;

    return (
      <div
        ref={forwardedRef}
        data-testid={dataTestId}
        className={cx(
          'flex',
          'flex-row',
          'justify-end',
          'gap-2',
          'py-6',
          'px-[var(--dialog-padding-x)]',
          className,
        )}
        {...rest}
      />
    );
  },
);
